/**
 * Geometria planului de etaj (Connection Quality, faza 5). Functii pure.
 *
 * TREI UNITATI, si unde sta fiecare:
 *  - pixeli ai imaginii (dupa micsorarea de pe server), de la coltul din
 *    stanga-sus, y in jos - ce vede omul si unde apasa;
 *  - cm, tot de la coltul din stanga-sus - ce se salveaza (`net_floor_items`,
 *    pereti, puncte de survey);
 *  - metri - in care se socoteste harta (grila, IDW, pierderea pe drum).
 *
 * Legatura dintre ele e SCARA (`scale_px_per_m`), din calibrare: omul apasa
 * doua puncte si scrie distanta reala dintre ele. Serverul o socoteste la fel
 * (`FloorLayout::calibration`, rotunjita la 4 zecimale), ca cele doua sa nu
 * se desparta.
 */

export type Pt = { x: number; y: number }

export type Calib = { x1: number; y1: number; x2: number; y2: number; meters: number }

/** Sub atatia pixeli intre cele doua puncte, calibrarea nu inseamna nimic. */
export const MIN_CALIB_PX = 5

/** Scara in pixeli pe metru, sau null daca calibrarea nu se poate folosi. */
export function scaleFromCalib(c: Calib | null | undefined): number | null {
  if (!c || !(c.meters > 0) || !Number.isFinite(c.meters)) return null
  const px = Math.hypot(c.x2 - c.x1, c.y2 - c.y1)
  if (!(px >= MIN_CALIB_PX)) return null
  return Math.round((px / c.meters) * 10000) / 10000
}

export function pxToCm(px: number, scale: number): number {
  return Math.round((px / scale) * 100)
}

export function cmToPx(cm: number, scale: number): number {
  return (cm * scale) / 100
}

export function pxToM(px: number, scale: number): number {
  return px / scale
}

/** Distanta in metri intre doua puncte date in pixeli. */
export function distanceM(a: Pt, b: Pt, scale: number): number {
  return Math.hypot(b.x - a.x, b.y - a.y) / scale
}

/**
 * Scara s-a schimbat (recalibrare): ce era pus pe plan ramane pe ACELASI
 * pixel, deci cm se inmultesc cu veche / noua. Serverul face la fel pentru
 * punctele de survey (`rescaleSpots`).
 */
export function rescaleCm(cm: number, oldScale: number, newScale: number): number {
  return Math.round((cm * oldScale) / newScale)
}

/** Marimea planului in metri. */
export function planSizeM(widthPx: number, heightPx: number, scale: number): { w: number; h: number } {
  return { w: widthPx / scale, h: heightPx / scale }
}

/**
 * Se taie segmentele [p1,p2] si [p3,p4]? Capetele atinse se socotesc taiere
 * (un perete care se opreste fix in linia dintre AP si punct tot atenueaza).
 * Segmentele coliniare nu se socotesc: raza care merge de-a lungul unui
 * perete nu trece prin el.
 */
export function segmentsIntersect(p1: Pt, p2: Pt, p3: Pt, p4: Pt): boolean {
  const d1x = p2.x - p1.x
  const d1y = p2.y - p1.y
  const d2x = p4.x - p3.x
  const d2y = p4.y - p3.y
  const den = d1x * d2y - d1y * d2x
  if (Math.abs(den) < 1e-12) return false
  const t = ((p3.x - p1.x) * d2y - (p3.y - p1.y) * d2x) / den
  const u = ((p3.x - p1.x) * d1y - (p3.y - p1.y) * d1x) / den
  const eps = 1e-9
  return t >= -eps && t <= 1 + eps && u >= -eps && u <= 1 + eps
}

/** Punctul cel mai apropiat de pe o lista de puncte (pentru "snap" pe capetele peretilor). */
export function nearestPoint(p: Pt, points: Pt[], maxDist: number): Pt | null {
  let best: Pt | null = null
  let bestD = maxDist
  for (const q of points) {
    const d = Math.hypot(q.x - p.x, q.y - p.y)
    if (d <= bestD) {
      best = q
      bestD = d
    }
  }
  return best
}
